import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { X, Plus, Trash2, GripHorizontal } from 'lucide-react';

const DIRECTIONS = [
  { value: 'R', label: 'R (Right)' },
  { value: 'L', label: 'L (Left)' },
  { value: 'S', label: 'S (Stay)' },
];

function applyBlankShortcut(val) {
  return val.replace(/\\_/g, '\u25A1').replace(/^_$/, '\u25A1');
}

export function TmTransitionEditor({
  transition,
  fromStateName,
  toStateName,
  position,
  onSave,
  onCancel,
}) {
  const [rules, setRules] = useState([]);
  const containerRef = useRef(null);
  const firstInputRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const [dragPos, setDragPos] = useState(null);

  useEffect(() => {
    const initial = transition?.tm_rules?.length
      ? transition.tm_rules.map(r => ({ read: r.read || '', write: r.write || '', direction: r.direction || 'R' }))
      : [{ read: '', write: '', direction: 'R' }];
    setRules(initial);
    setTimeout(() => firstInputRef.current?.focus(), 50);
  }, [transition]);

  const updateRule = useCallback((idx, field, value) => {
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, [field]: field === 'direction' ? value : applyBlankShortcut(value) } : r));
  }, []);

  const addRule = useCallback(() => {
    setRules(prev => [...prev, { read: '', write: '', direction: 'R' }]);
  }, []);

  const removeRule = useCallback((idx) => {
    setRules(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  }, []);

  const handleSave = useCallback(() => {
    const validRules = rules.filter(r => r.read.length === 1);
    if (validRules.length === 0) return;
    onSave(validRules);
  }, [rules, onSave]);

  // Focus the read input of a newly added rule
  useEffect(() => {
    if (rules.length > 1) {
      const lastIdx = rules.length - 1;
      const el = containerRef.current?.querySelector(`[data-testid="tm-rule-read-${lastIdx}"]`);
      if (el && !rules[lastIdx].read && !rules[lastIdx].write) {
        el.focus();
      }
    }
  }, [rules.length]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  }, [handleSave, onCancel]);

  // Tab from last direction select → add new rule and focus its read input
  const handleSelectKeyDown = useCallback((e, idx) => {
    if (e.key === 'Tab' && !e.shiftKey && idx === rules.length - 1) {
      e.preventDefault();
      addRule();
    }
  }, [rules.length, addRule]);

  // --- Drag logic ---
  const onDragPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const panel = containerRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: dragPos ? dragPos.x : rect.left,
      origY: dragPos ? dragPos.y : rect.top,
    };
    const onMove = (me) => {
      if (!dragState.current.dragging) return;
      const dx = me.clientX - dragState.current.startX;
      const dy = me.clientY - dragState.current.startY;
      setDragPos({ x: dragState.current.origX + dx, y: dragState.current.origY + dy });
    };
    const onUp = () => {
      dragState.current.dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [dragPos]);

  // Position the popup — dragged position takes priority
  const style = dragPos
    ? { position: 'fixed', left: dragPos.x, top: dragPos.y, zIndex: 1000 }
    : position
    ? { position: 'fixed', left: Math.min(position.x, window.innerWidth - 340), top: Math.min(position.y - 20, window.innerHeight - 300), zIndex: 1000 }
    : {};

  return (
    <>
      <div className="fixed inset-0 z-[999]" onClick={onCancel} />
      <div
        ref={containerRef}
        className="bg-popover border-2 border-border rounded-xl shadow-xl p-3 w-[280px]"
        style={style}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        data-testid="tm-transition-editor"
      >
        {/* Drag handle + header */}
        <div
          className="flex items-center justify-between mb-2 cursor-grab active:cursor-grabbing select-none"
          onPointerDown={onDragPointerDown}
          data-testid="tm-editor-drag-handle"
        >
          <div className="flex items-center gap-1.5">
            <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground/50" />
            <span className="text-xs font-semibold text-muted-foreground">
              {fromStateName} &rarr; {toStateName}
            </span>
          </div>
          <button onClick={onCancel} className="p-0.5 hover:bg-muted rounded" onPointerDown={(e) => e.stopPropagation()}>
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-[40px_40px_1fr_24px] gap-2 mb-1">
          <span className="text-[10px] font-medium text-muted-foreground px-1">Read</span>
          <span className="text-[10px] font-medium text-muted-foreground px-1">Write</span>
          <span className="text-[10px] font-medium text-muted-foreground px-1">Move</span>
          <span />
        </div>

        {/* Rules */}
        {rules.map((rule, idx) => (
          <div key={idx} className="grid grid-cols-[40px_40px_1fr_24px] gap-2 mb-1.5 items-center">
            <input
              ref={idx === 0 ? firstInputRef : undefined}
              type="text"
              value={rule.read}
              onChange={(e) => updateRule(idx, 'read', e.target.value.slice(-1))}
              placeholder="_"
              maxLength={1}
              className="w-10 h-7 text-sm font-mono bg-muted/50 border border-border/50 rounded text-center focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid={`tm-rule-read-${idx}`}
            />
            <input
              type="text"
              value={rule.write}
              onChange={(e) => updateRule(idx, 'write', e.target.value.slice(-1))}
              placeholder="_"
              maxLength={1}
              className="w-10 h-7 text-sm font-mono bg-muted/50 border border-border/50 rounded text-center focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid={`tm-rule-write-${idx}`}
            />
            <select
              value={rule.direction}
              onChange={(e) => updateRule(idx, 'direction', e.target.value)}
              onKeyDown={(e) => handleSelectKeyDown(e, idx)}
              className="w-full h-7 px-2 text-sm bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid={`tm-rule-dir-${idx}`}
            >
              {DIRECTIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            <button
              onClick={() => removeRule(idx)}
              className="p-0.5 hover:bg-muted rounded transition-colors"
              tabIndex={-1}
              disabled={rules.length <= 1}
              data-testid={`tm-rule-remove-${idx}`}
            >
              <Trash2 className={`w-3.5 h-3.5 ${rules.length <= 1 ? 'text-muted-foreground/30' : 'text-muted-foreground'}`} />
            </button>
          </div>
        ))}

        {/* Actions */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={addRule} tabIndex={-1} data-testid="tm-add-rule-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Rule
          </Button>
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onCancel} tabIndex={-1}>Cancel</Button>
            <Button size="sm" className="h-6 text-xs" onClick={handleSave} tabIndex={-1} data-testid="tm-save-rules-btn">Save</Button>
          </div>
        </div>
      </div>
    </>
  );
}
